// Redux Settings module
// see https://github.com/erikras/ducks-modular-redux

import { Settings, Utils, Events } from '../../services/';
import { isCinemaCustomerType } from '../../components/DateRangePresets/dateRangePresets';
import { Setting } from '../../models/';
import { v4 } from 'uuid';
import i18n from '../../locales/i18n';

const AUTH_LOG_OUT = 'kronos/auth/LOG_OUT';

const LOADING_SETTING            = 'kronos/settings/LOADING_SETTING';
export const LOADING_SETTING_SUCCESS = 'kronos/settings/LOADING_SETTING_SUCCESS';
const LOADING_SETTING_FAILURE    = 'kronos/settings/LOADING_SETTING_FAILURE';
const CHANGE_SETTING             = 'kronos/settings/CHANGE_SETTING';
const UPDATING_SETTING           = 'kronos/settings/UPDATING_SETTING';
export const UPDATING_SETTING_SUCCESS   = 'kronos/settings/UPDATING_SETTING_SUCCESS';
const UPDATING_SETTING_FAILURE   = 'kronos/settings/UPDATING_SETTING_FAILURE';
const ADD_DELIVERY_TYPE          = 'kronos/settings/ADD_DELIVERY_TYPE';
const REMOVE_DELIVERY_TYPE       = 'kronos/settings/REMOVE_DELIVERY_TYPE';
const ADD_DELIVERY_TYPE_RULE     = 'kronos/settings/ADD_DELIVERY_TYPE_RULE';
const REMOVE_DELIVERY_TYPE_RULE  = 'kronos/settings/REMOVE_DELIVERY_TYPE_RULE';
const ADD_PAYMENT_METHOD         = 'kronos/settings/ADD_PAYMENT_METHOD';
const REMOVE_PAYMENT_METHOD      = 'kronos/settings/REMOVE_PAYMENT_METHOD';
const ADD_PAYMENT_METHOD_RULE    = 'kronos/settings/ADD_PAYMENT_METHOD_RULE';
const REMOVE_PAYMENT_METHOD_RULE = 'kronos/settings/REMOVE_PAYMENT_METHOD_RULE';
const ADD_IMAGE                  = 'kronos/settings/ADD_IMAGE';
const REMOVE_IMAGE               = 'kronos/settings/REMOVE_IMAGE';
const COPY_MOBILE_SETTINGS       = 'kronos/settings/COPY_MOBILE_SETTINGS';
const LOADING_OWN_MOVIES         = 'kronos/settings/LOADING_OWN_MOVIES';
const LOADING_OWN_MOVIES_SUCCESS = 'kronos/settings/LOADING_OWN_MOVIES_SUCCESS';
const LOADING_OWN_MOVIES_FAILURE = 'kronos/settings/LOADING_OWN_MOVIES_FAILURE';

const initialState = {
    isLoading: false,
    hasLoadingError: false,
    loadingErrorMessage: '',
    loadingRequestId: null,
    hasUpdatingError: false,
    updatingErrorMessage: '',
    settingSuccessfullyCreated: false,
    settings: [],
    setting: {},
    hasOwnMovies: null,
    isLoadingOwnMovies: false,
}

const initialDeliveryTypeRule = (currency) => {
    return {
        price: { [currency]: 0 },
        countries: [ 'CH' ]
    }
};

const initialDeliveryType = (currency, languages) => {
    const type = {
        _id: v4(),
        type: Utils.DELIVERY_TYPE_SHIPMENT,
        name: {},
        rules: [initialDeliveryTypeRule(currency)],
    };

    languages.forEach(language => {
        type.name[language] = i18n.t('settings.delivery.type.new_method');
    });

    return type;
};

const initialPaymentMethodRule = (currency) => {
    return {
        price: {
            [currency]: 0,
            percentage: 0
        },
        countries: [ 'CH' ]
    }
};

const initialImage = source => {
    return {
        type: source?.type || Utils.IMAGE_TYPE_LOGO,
        lang: source?.lang || null,
        rfc2397_data: source?.rfc2397_data || null
    }
};

// Reducer
export default function reducer(state = initialState, action) {
    switch(action.type) {
        case AUTH_LOG_OUT:
            return {
                ...state,
                hasOwnMovies: null,
                isLoadingOwnMovies: false,
            };
        case LOADING_OWN_MOVIES:
            return {
                ...state,
                isLoadingOwnMovies: true,
            };
        case LOADING_OWN_MOVIES_SUCCESS:
            return {
                ...state,
                isLoadingOwnMovies: false,
                hasOwnMovies: action.hasOwnMovies,
            };
        case LOADING_OWN_MOVIES_FAILURE:
            return {
                ...state,
                isLoadingOwnMovies: false,
                hasOwnMovies: false,
            };
        case LOADING_SETTING:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false,
                loadingErrorMessage: '',
                hasUpdatingError: false,
                updatingErrorMessage: '',
                loadingRequestId: action.requestId,
            };
        case LOADING_SETTING_SUCCESS:
            if (state.loadingRequestId !== action.requestId)
                return state;
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                loadingRequestId: null,
                setting: new Setting(action.setting)
            };
        case LOADING_SETTING_FAILURE:
            if (state.loadingRequestId !== action.requestId)
                return state;
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingRequestId: null,
                loadingErrorMessage: action.error.message
            };
        case CHANGE_SETTING:
            return {
                ...state,
                setting: new Setting(action.setting)
            };
        case COPY_MOBILE_SETTINGS:
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                    mobile: {
                        ...state.setting.mobile,
                        [action.destination]: { ...state.setting.mobile[action.src] }
                    }
                })
            };
        case UPDATING_SETTING:
            return {
                ...state,
                isLoading: true,
                hasUpdatingError: false,
                updatingErrorMessage: '',
            };
        case UPDATING_SETTING_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasUpdatingError: false,
                updatingErrorMessage: '',
                setting: new Setting(action.setting)
            };
        case UPDATING_SETTING_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasUpdatingError: true,
                updatingErrorMessage: action.error?.message || '',
            };
        case ADD_DELIVERY_TYPE:
            const types = [
                ...(state.setting.delivery?.types || []),
                initialDeliveryType(action.currency, action.languages),
            ];
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                    delivery: {
                        ...state.setting.delivery,
                        types
                    }
                })
            };
        case REMOVE_DELIVERY_TYPE:
            const newTypes = (state.setting.delivery?.types || []).filter((type, index) => index !== action.typeIndex);
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                    delivery: {
                        ...state.setting.delivery,
                        types: newTypes
                    }
                })
            };
        case ADD_DELIVERY_TYPE_RULE:
            const typesWithNewRule = (state.setting.delivery?.types || []).map((type, index) => {
                if (index !== action.typeIndex)
                    return type;

                return {
                    ...type,
                    rules: [ ...(type.rules || []), initialDeliveryTypeRule(action.currency) ]
                };
            });
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                    delivery: {
                        ...state.setting.delivery,
                        types: typesWithNewRule
                    }
                })
            };
        case REMOVE_DELIVERY_TYPE_RULE:
            const updatedTypes = (state.setting.delivery?.types || []).map((type, index) => {
                if (index !== action.typeIndex)
                    return type;

                return {
                    ...type,
                    rules: (type.rules || []).filter((rule, ruleIndex) => ruleIndex !== action.ruleIndex)
                };
            });
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                    delivery: {
                        ...state.setting.delivery,
                        types: updatedTypes
                    }
                })
            };
        case ADD_PAYMENT_METHOD:
            return state;
        case REMOVE_PAYMENT_METHOD:
            return state;
        case ADD_PAYMENT_METHOD_RULE:
            const methodsWithNewRule = (state.setting.payment?.methods || []).map((method, index) => {
                if (index !== action.methodIndex)
                    return method;

                return {
                    ...method,
                    rules: [ ...(method.rules || []), initialPaymentMethodRule(action.currency) ]
                };
            });
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                    payment: {
                        ...state.setting.payment,
                        methods: methodsWithNewRule
                    }
                })
            };
        case REMOVE_PAYMENT_METHOD_RULE:
            const updatedMethods = (state.setting.payment?.methods || []).map((method, index) => {
                if (index !== action.methodIndex)
                    return method;

                return {
                    ...method,
                    rules: (method.rules || []).filter((rule, ruleIndex) => ruleIndex !== action.ruleIndex)
                };
            });
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                    payment: {
                        ...state.setting.payment,
                        methods: updatedMethods
                    }
                })
            };
        case ADD_IMAGE:
            const images = [
                ...(state.setting.images || []),
                initialImage(action.source),
            ];
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                    images
                })
            };
        case REMOVE_IMAGE:
            const newImages = (state.setting.images || []).filter((image, index) => index !== action.imageIndex);
            return {
                ...state,
                setting: new Setting({
                    ...state.setting,
                   images: newImages
                })
            };
        default:
            return state;
    }
};

// Actions
function loadingSetting(requestId) { return { type: LOADING_SETTING, requestId } }
function loadingSettingSuccess(setting, requestId) { return { type: LOADING_SETTING_SUCCESS, setting: setting, requestId } }
function loadingSettingFailure(err, requestId) { return { type: LOADING_SETTING_FAILURE, error: err, requestId } }
export function loadSetting(settingId) {
    return (dispatch) => {
        const requestId = v4();
        dispatch(loadingSetting(requestId));
        return Settings.get(settingId)
            .then(data => {
                const { setting } = data;
                dispatch(loadingSettingSuccess(setting, requestId));
                return data;
            })
            .catch(err => {
                dispatch(loadingSettingFailure(err, requestId));
                throw err;
            });
    }
}

/** Push an already-fetched setting document into the store (no extra GET). */
export function applySetting(setting) {
    return (dispatch) => {
        const requestId = v4();
        dispatch(loadingSetting(requestId));
        dispatch(loadingSettingSuccess(setting, requestId));
    };
}

function loadingOwnMovies() { return { type: LOADING_OWN_MOVIES }; }
function loadingOwnMoviesSuccess(hasOwnMovies) {
    return { type: LOADING_OWN_MOVIES_SUCCESS, hasOwnMovies };
}
function loadingOwnMoviesFailure() { return { type: LOADING_OWN_MOVIES_FAILURE }; }

/**
 * Lightweight check: instance has at least one own movie (Mes films).
 * Skips the API for cinema / cinema-festival instances (cinema week presets do not
 * depend on stored movies) and when the user cannot read events.
 */
export function loadHasOwnMovies(user, setting) {
    return (dispatch) => {
        if (isCinemaCustomerType(setting)) {
            dispatch(loadingOwnMoviesSuccess(true));
            return;
        }
        const can_read_events = Boolean(
            user?.abilities?.superadmin
            || (typeof user?.can === 'function' && user.can('read', 'events'))
        );
        if (!can_read_events) {
            dispatch(loadingOwnMoviesSuccess(false));
            return;
        }
        dispatch(loadingOwnMovies());
        Events.list(Utils.EVENT_TYPE_MOVIE, { page: 0, page_size: 1, fields: 'id' })
            .then((data) => {
                const movies = data.movies || data.events || [];
                dispatch(loadingOwnMoviesSuccess(movies.length > 0));
            })
            .catch(() => {
                dispatch(loadingOwnMoviesFailure());
            });
    };
}

function updatingSetting() { return { type: UPDATING_SETTING } }
function updatingSettingSuccess(setting) { return { type: UPDATING_SETTING_SUCCESS, setting: setting } }
function updatingSettingFailure(err) { return { type: UPDATING_SETTING_FAILURE, error: err } }
export function updateSetting(setting) {
    return (dispatch) => {
        if (!setting?._id || setting._id === 'undefined') {
            const err = new Error('Missing setting id');
            dispatch(updatingSettingFailure(err));
            return Promise.reject(err);
        }
        dispatch(updatingSetting());
        // Always return a promise (and catch sync JSON/serialize throws) so
        // redux-form submitting + isLoading cannot stick on failure.
        return Promise.resolve()
            .then(() => Settings.update(setting))
            .then(data => {
                dispatch(updatingSettingSuccess(data.setting));
                return data;
            })
            .catch(err => {
                dispatch(updatingSettingFailure(err));
                throw err;
            });
    }
}

export function addDeliveryType() {
    const languages = Utils.langs();
    const currency  = Utils.currency();
    return (dispatch) => {
        return dispatch({
            type: ADD_DELIVERY_TYPE,
            currency,
            languages
        });
    };
}
export function removeDeliveryType(typeIndex) {
    return (dispatch) => {
        return dispatch({ type: REMOVE_DELIVERY_TYPE, typeIndex });
    };
}

export function addDeliveryTypeRule(typeIndex) {
    const currency  = Utils.currency();
    return (dispatch) => {
        return dispatch({ type: ADD_DELIVERY_TYPE_RULE, typeIndex, currency });
    };
}
export function removeDeliveryTypeRule(typeIndex, ruleIndex) {
    return (dispatch) => {
        return dispatch({ type: REMOVE_DELIVERY_TYPE_RULE, typeIndex, ruleIndex });
    };
}

export function addPaymentMethod() {
    const languages = Utils.langs();
    const currency  = Utils.currency();
    return (dispatch) => {
        return dispatch({
            type: ADD_PAYMENT_METHOD,
            currency,
            languages
        });
    };
}
export function removePaymentMethod(methodIndex) {
    return (dispatch) => {
        return dispatch({ type: REMOVE_PAYMENT_METHOD, methodIndex });
    };
}

export function addPaymentMethodRule(methodIndex) {
    const currency  = Utils.currency();
    return (dispatch) => {
        return dispatch({ type: ADD_PAYMENT_METHOD_RULE, methodIndex, currency });
    };
}
export function removePaymentMethodRule(methodIndex, ruleIndex) {
    return (dispatch) => {
        return dispatch({ type: REMOVE_PAYMENT_METHOD_RULE, methodIndex, ruleIndex });
    };
}

export function addImage(source) {
    return (dispatch) => {
        return dispatch({ type: ADD_IMAGE, source });
    };
}
export function removeImage(imageIndex) {
    return (dispatch) => {
        return dispatch({ type: REMOVE_IMAGE, imageIndex });
    };
}

export function changeSetting(setting) {
    return (dispatch) => {
        return dispatch({ type: CHANGE_SETTING, setting });
    };
}

export function copyMobileSettings(src, destination) {
    return (dispatch) => {
        return dispatch({ type: COPY_MOBILE_SETTINGS, src, destination });
    };
}
